"use client";
import { FollowEmailModal } from "@/app/(citizen)/votacao/modals/follow-email-modal";
import { RemoveInteractionModal } from "@/app/(citizen)/votacao/modals/remove-interaction-modal";
import deleteAcompanhamento from "@/app/actions/delete-acompanhamento";
import deleteApoio from "@/app/actions/delete-apoio";
import { ProblemItem } from "@/app/actions/get-problems";
import postAcompanhamento from "@/app/actions/post-acompanhamento";
import postApoio from "@/app/actions/post-apoio";
import { Icon } from "@/components";
import { CommentSolidIcon, LikeSolidIcon } from "@/icons";
import { cn } from "@/lib/utils";
import { useCidadao } from "@/providers/cidadao-provider";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CommentDisabledModal } from "../../app/(citizen)/votacao/modals/comment-disabled-modal";
import { LikeModal } from "../../app/(citizen)/votacao/modals/like-modal";

interface InteractionProps {
  count?: number;
  active: boolean;
  size?: number;
  onClick?: (active: boolean) => void;
}

interface InteractionButtonProps {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
  type: string;
}

export type InteractionType = "votar" | "relatar" | "acompanhar";

function InteractionButton({
  children,
  active,
  type,
  onClick,
}: InteractionButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex gap-1 items-center justify-center text-xs cursor-pointer h-10 hover:bg-muted transition-all duration-200 ease-in-out rounded-lg px-1 lg:px-2 active:bg-border/40 flex-1",
        `group/${type}`,
        active ? "text-primary" : "text-secondary"
      )}
    >
      {children}
    </button>
  );
}

export function Likes({
  count = 0,
  active,
  onClick = () => {},
}: InteractionProps) {
  return active ? (
    <InteractionButton
      active={active}
      onClick={() => onClick(false)}
      type="like"
    >
      <LikeSolidIcon className="fill-primary w-4.5 h-4.5 group-active/like:animate-swing" />
      {count > 0 && count} Voto{count != 1 && "s"}
    </InteractionButton>
  ) : (
    <InteractionButton
      active={active}
      onClick={() => onClick(true)}
      type="like"
    >
      <LikeSolidIcon className="fill-secondary w-4.5 h-4.5 group-active/like:animate-swing" />{" "}
      Votar
    </InteractionButton>
  );
}

export function Comments({
  count = 0,
  active,
  onClick = () => {},
}: InteractionProps) {
  return active ? (
    <InteractionButton
      active={active}
      onClick={() => onClick(false)}
      type="comment"
    >
      <CommentSolidIcon className="fill-primary w-4.5 h-4.5 group-active/comment:animate-swing" />
      {count > 0 && count} Relato{count != 1 && "s"}
    </InteractionButton>
  ) : (
    <InteractionButton
      active={active}
      onClick={() => onClick(true)}
      type="comment"
    >
      <CommentSolidIcon className="fill-secondary w-4.5 h-4.5 group-active/comment:animate-swing" />{" "}
      {count > 0 ? `${count} Relato${count != 1 ? "s" : ""}` : "Relatar"}
    </InteractionButton>
  );
}

export function Follow({ active, onClick = () => {} }: InteractionProps) {
  return active ? (
    <InteractionButton
      active={active}
      onClick={() => onClick(false)}
      type="support"
    >
      <Icon
        icon="IoNotifications"
        className="fill-primary w-4.5 h-4.5 group-active/support:animate-swing"
      />{" "}
      Acompanhar
    </InteractionButton>
  ) : (
    <InteractionButton
      active={active}
      onClick={() => onClick(true)}
      type="support"
    >
      <Icon
        icon="IoNotifications"
        className="fill-secondary w-4.5 h-4.5 group-active/support:animate-swing"
      />{" "}
      Acompanhar
    </InteractionButton>
  );
}

interface InteractionsProps {
  problem: ProblemItem;
  pathName?: string;
  likeCallback?: () => void;
  followCallback?: () => void;
}
export function Interactions({
  problem,
  likeCallback,
  followCallback,
  pathName = "/votacao",
}: InteractionsProps) {
  const { cidadao, refetch, likes, setLikes, setIsModalOpen } = useCidadao();
  const router = useRouter();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [isCommented, setIsCommented] = useState(false);
  const [isFollowed, setIsFollowed] = useState(false);
  const [openLikeModal, setOpenLikeModal] = useState(false);
  const [openCommentDisabledModal, setOpenCommentDisabledModal] =
    useState(false);
  const [openFollowEmailModal, setOpenFollowEmailModal] = useState(false);
  const [openRemoveInteractionModal, setOpenRemoveInteractionModal] =
    useState(false);
  const [interaction, setInteraction] = useState<InteractionType>("votar");

  async function handleLikeClick() {
    if (isProcessing) return;
    setIsProcessing(true);
    if (!cidadao) {
      setIsModalOpen(true);
      setInteraction("votar");
    } else if (!isLiked) {
      if (likes < 5) {
        const response = await postApoio(cidadao?.id_gov, problem.id);
        if (response) {
          setLikes((prev) => prev + 1);
          setIsLiked(true);
          setOpenLikeModal(true);
        } else toast.error("Erro ao votar.");
      } else {
        toast.error("Você já votou em 5 problemas.");
      }
    } else {
      setInteraction("votar");
      setOpenRemoveInteractionModal(true);
    }
    setIsProcessing(false);
  }

  async function handleCommentClick() {
    if (isProcessing) return;
    setIsProcessing(true);
    if (!cidadao) {
      setIsModalOpen(true);
      setInteraction("relatar");
    } else if (!isLiked) {
      if (likes < 5) setOpenCommentDisabledModal(true);
      else toast.error("Você já votou em 5 problemas.");
    } else router.push(`${pathName}/problemas/${problem.id}?relato=true`);
    setIsProcessing(false);
  }

  async function followProblem() {
    const response = await postAcompanhamento(
      cidadao?.id_gov ?? "",
      problem.id
    );
    if (response) {
      setIsFollowed(true);
      if (!cidadao?.email || cidadao.email === "") refetch();
      toast.success("Problema acompanhado com sucesso.");
    } else toast.error("Erro ao acompanhar o problema.");
  }

  async function handleFollowClick() {
    if (isProcessing) return;
    setIsProcessing(true);
    if (!cidadao) {
      setIsModalOpen(true);
      setInteraction("acompanhar");
    } else if (!isFollowed) {
      if (!cidadao.email || cidadao.email === "") {
        setOpenFollowEmailModal(true);
      } else followProblem();
    } else {
      setInteraction("acompanhar");
      setOpenRemoveInteractionModal(true);
    }
    setIsProcessing(false);
  }

  async function handleRemoveInteraction() {
    setOpenRemoveInteractionModal(false);
    if (interaction === "votar") {
      const response = await deleteApoio(cidadao?.id_gov ?? "", problem.id);
      if (response) {
        setLikes((prev) => prev - 1);
        setIsLiked(false);
        if (likeCallback) likeCallback();
      } else toast.error("Erro ao remover o voto.");
    } else if (interaction === "acompanhar") {
      const response = await deleteAcompanhamento(
        cidadao?.id_gov ?? "",
        problem.id
      );
      if (response) {
        setIsFollowed(false);
        if (followCallback) followCallback();
      } else toast.error("Erro ao parar de acompanhar o problema.");
    }
  }

  useEffect(() => {
    if (isFirstRender) return;
    if (!!cidadao && !!problem) setIsFirstRender(true);
    if (cidadao?.problemas_apoiados.some((p) => p.id === problem.id))
      setIsLiked(true);
    if (
      cidadao?.comentarios.some((comment) => comment.id_problema === problem.id)
    )
      setIsCommented(true);
    if (cidadao?.problemas_acompanhados.some((p) => p.id === problem.id))
      setIsFollowed(true);
  }, [cidadao, problem, isFirstRender]);

  if (!problem) return null;
  return (
    <>
      <div className="flex justify-between items-center gap-1 pt-1 w-full">
        <Likes
          active={isLiked}
          onClick={handleLikeClick}
          count={problem.apoios}
        />
        <Comments
          active={isLiked && isCommented}
          onClick={handleCommentClick}
          count={isLiked ? problem.relatos : undefined}
        />
        <Follow active={isFollowed} onClick={handleFollowClick} />
      </div>

      <RemoveInteractionModal
        isOpen={openRemoveInteractionModal}
        onOpenChange={setOpenRemoveInteractionModal}
        interaction={interaction}
        onAccept={handleRemoveInteraction}
      />
      <FollowEmailModal
        isOpen={openFollowEmailModal}
        onOpenChange={setOpenFollowEmailModal}
        id_gov={cidadao?.id_gov ?? ""}
        callback={followProblem}
      />
      <LikeModal
        isOpen={openLikeModal}
        onOpenChange={setOpenLikeModal}
        id={problem.id}
      />
      <CommentDisabledModal
        isOpen={openCommentDisabledModal}
        onOpenChange={setOpenCommentDisabledModal}
        id={problem.id}
        onAccept={() => {
          setIsLiked(true);
          setLikes((prev) => prev + 1);
          toast.success("Voto registrado com sucesso.");
        }}
      />
    </>
  );
}
